import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { toast } from "sonner";
import { Loader2, ImagePlus, Star, Trash2, ArrowLeft, ArrowRight, Eye, EyeOff, ImageUp, Pencil, Check, X, GripVertical } from "lucide-react";
import { L, Card, SectionTitle } from "./shared";

export default function GalleryTab({ pid, meta, onCoverChanged }) {
  const [photos, setPhotos] = useState(null);
  const [queue, setQueue] = useState([]);           // [{name, status, error}]
  const [uploading, setUploading] = useState(false);
  const [defaults, setDefaults] = useState({ category: "Șantier", month: new Date().toISOString().slice(0, 7), caption: "" });
  const [editing, setEditing] = useState(null);     // photo id
  const [editF, setEditF] = useState({});
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef(null);
  const cats = meta?.photo_categories || ["Șantier", "Altele"];

  async function load() {
    try { const { data } = await api.get(`/staff/client-config/${pid}/photos`); setPhotos(data); }
    catch (e) { toast.error(apiErr(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  async function uploadFiles(files) {
    const list = Array.from(files || []); if (!list.length) return;
    setUploading(true);
    setQueue(list.map(f => ({ name: f.name, status: "waiting" })));
    for (let i = 0; i < list.length; i++) {
      setQueue(q => q.map((x, j) => j === i ? { ...x, status: "uploading" } : x));
      const fd = new FormData();
      fd.append("file", list[i]); fd.append("caption", defaults.caption); fd.append("category", defaults.category); fd.append("month", defaults.month);
      try {
        await api.post(`/staff/client-config/${pid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue(q => q.map((x, j) => j === i ? { ...x, status: "done" } : x));
      } catch (e) {
        setQueue(q => q.map((x, j) => j === i ? { ...x, status: "error", error: apiErr(e) } : x));
      }
    }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
    await load(); toast.success("Încărcare finalizată");
    setTimeout(() => setQueue([]), 4000);
  }

  async function patch(ph, data) {
    try { const { data: upd } = await api.put(`/staff/client-config/${pid}/photos/${ph.id}`, data); setPhotos(ps => ps.map(p => p.id === ph.id ? upd : p).sort(sortKey)); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function del(ph) {
    if (!window.confirm("Ștergi această fotografie din galerie?")) return;
    try { await api.delete(`/staff/client-config/${pid}/photos/${ph.id}`); setPhotos(ps => ps.filter(p => p.id !== ph.id)); toast.success("Fotografie ștearsă"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function setCover(ph) {
    try { await api.post(`/staff/client-config/${pid}/photos/${ph.id}/set-cover`); toast.success("Setată drept cover"); onCoverChanged && onCoverChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function persistOrder(list) {
    const ordered = list.map((p, i) => ({ ...p, sort_order: i }));
    setPhotos(ordered);
    try { await api.put(`/staff/client-config/${pid}/photos/reorder`, { ids: ordered.map(p => p.id) }); }
    catch (e) { toast.error(apiErr(e)); load(); }
  }
  function move(idx, dir) {
    const list = [...photos]; const j = idx + dir; if (j < 0 || j >= list.length) return;
    [list[idx], list[j]] = [list[j], list[idx]]; persistOrder(list);
  }
  function onDrop(targetId) {
    if (!dragId || dragId === targetId) return;
    const list = [...photos]; const from = list.findIndex(p => p.id === dragId); const to = list.findIndex(p => p.id === targetId);
    const [item] = list.splice(from, 1); list.splice(to, 0, item); setDragId(null); persistOrder(list);
  }
  function startEdit(ph) { setEditing(ph.id); setEditF({ caption: ph.caption || "", category: ph.category || "", month: ph.month || "" }); }
  async function saveEdit(ph) { await patch(ph, editF); setEditing(null); }

  if (!photos) return <div className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-5 w-5 animate-spin" /> Se încarcă...</div>;

  return (
    <div className="space-y-6" data-testid="cc-gallery">
      <Card>
        <SectionTitle sub="Poți selecta mai multe fotografii deodată (max 15 MB fiecare). Se generează automat thumbnail-uri.">Încarcă fotografii</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_1fr_auto] gap-3 items-end">
          <L label="Categorie implicită"><select value={defaults.category} onChange={e => setDefaults({ ...defaults, category: e.target.value })} className="inp" data-testid="gal-cat">{cats.map(c => <option key={c}>{c}</option>)}</select></L>
          <L label="Luna"><input type="month" value={defaults.month} onChange={e => setDefaults({ ...defaults, month: e.target.value })} className="inp" data-testid="gal-month" /></L>
          <L label="Descriere implicită (opțional)"><input value={defaults.caption} onChange={e => setDefaults({ ...defaults, caption: e.target.value })} className="inp" placeholder="Se poate edita ulterior pe fiecare poză" /></L>
          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => uploadFiles(e.target.files)} data-testid="gal-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gal-upload"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60 whitespace-nowrap">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Alege fotografii
            </button>
          </div>
        </div>
        {queue.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="gal-queue">
            {queue.map((q, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px]">
                <span className={`h-2 w-2 rounded-full ${q.status === "done" ? "bg-emerald-500" : q.status === "error" ? "bg-red-500" : q.status === "uploading" ? "bg-amber-400 animate-pulse" : "bg-neutral-300"}`} />
                <span className="truncate flex-1">{q.name}</span>
                <span className="text-ink-muted">{q.status === "done" ? "încărcat" : q.status === "error" ? q.error : q.status === "uploading" ? "se încarcă..." : "în așteptare"}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle sub="Trage fotografiile pentru a schimba ordinea sau folosește săgețile. Cele „featured” apar primele în portalul clientului.">
          Galerie ({photos.length})
        </SectionTitle>
        {photos.length === 0 ? (
          <div className="py-12 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">Nicio fotografie încă.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="gal-grid">
            {photos.map((ph, idx) => (
              <div key={ph.id} draggable onDragStart={() => setDragId(ph.id)} onDragOver={e => e.preventDefault()} onDrop={() => onDrop(ph.id)}
                className={`group border rounded-xl overflow-hidden bg-white ${dragId === ph.id ? "opacity-50" : ""} ${ph.featured ? "border-amber-400" : "border-neutral-200"} ${ph.visible_to_client === false ? "opacity-70" : ""}`}
                data-testid={`gal-item-${ph.id}`}>
                <div className="relative aspect-[4/3] bg-neutral-100">
                  <img src={mediaUrl(ph.thumbnail_url || ph.url)} alt={ph.caption} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute top-2 left-2 flex gap-1">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded bg-black/50 text-white cursor-grab"><GripVertical className="h-3.5 w-3.5" /></span>
                    <span className="px-1.5 h-6 inline-flex items-center rounded bg-black/50 text-white text-[10px] font-mono">#{idx + 1}</span>
                  </div>
                  <button onClick={() => patch(ph, { featured: !ph.featured })} title="Featured" data-testid={`gal-feat-${ph.id}`}
                    className={`absolute top-2 right-2 h-7 w-7 rounded-full flex items-center justify-center ${ph.featured ? "bg-amber-400 text-white" : "bg-black/50 text-white hover:bg-amber-400"}`}>
                    <Star className="h-3.5 w-3.5" fill={ph.featured ? "currentColor" : "none"} />
                  </button>
                  {ph.visible_to_client === false && <div className="absolute inset-x-0 bottom-0 bg-red-600/80 text-white text-[10px] font-mono uppercase text-center py-0.5">ascunsă clientului</div>}
                </div>
                <div className="p-3">
                  {editing === ph.id ? (
                    <div className="space-y-2" data-testid={`gal-edit-${ph.id}`}>
                      <input value={editF.caption} onChange={e => setEditF({ ...editF, caption: e.target.value })} className="inp" placeholder="Descriere / caption" data-testid="gal-edit-caption" />
                      <div className="grid grid-cols-2 gap-2">
                        <select value={editF.category} onChange={e => setEditF({ ...editF, category: e.target.value })} className="inp">{[...new Set([...cats, editF.category].filter(Boolean))].map(c => <option key={c}>{c}</option>)}</select>
                        <input type="month" value={editF.month} onChange={e => setEditF({ ...editF, month: e.target.value })} className="inp" />
                      </div>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditing(null)} className="p-1.5 text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>
                        <button onClick={() => saveEdit(ph)} className="p-1.5 text-emerald-600" data-testid="gal-edit-save"><Check className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-[13px] text-ink font-medium truncate min-h-[18px]">{ph.caption || <span className="text-ink-muted italic">fără descriere</span>}</div>
                      <div className="text-[11px] text-ink-muted font-mono">{ph.category} · {ph.month}</div>
                      <div className="flex items-center gap-0.5 mt-2 text-ink-muted">
                        <button onClick={() => move(idx, -1)} title="Mută la stânga" className="p-1.5 hover:text-ink disabled:opacity-30" disabled={idx === 0}><ArrowLeft className="h-3.5 w-3.5" /></button>
                        <button onClick={() => move(idx, 1)} title="Mută la dreapta" className="p-1.5 hover:text-ink disabled:opacity-30" disabled={idx === photos.length - 1}><ArrowRight className="h-3.5 w-3.5" /></button>
                        <button onClick={() => startEdit(ph)} title="Editează" className="p-1.5 hover:text-ink" data-testid={`gal-editbtn-${ph.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => patch(ph, { visible_to_client: ph.visible_to_client === false })} title={ph.visible_to_client === false ? "Arată clientului" : "Ascunde de client"} className="p-1.5 hover:text-ink">
                          {ph.visible_to_client === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                        {ph.url?.startsWith("/api/") && <button onClick={() => setCover(ph)} title="Setează ca cover" className="p-1.5 hover:text-brand" data-testid={`gal-cover-${ph.id}`}><ImageUp className="h-3.5 w-3.5" /></button>}
                        <button onClick={() => del(ph)} title="Șterge" className="p-1.5 hover:text-red-600 ml-auto" data-testid={`gal-del-${ph.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const sortKey = (a, b) => (a.featured === b.featured ? (a.sort_order ?? 0) - (b.sort_order ?? 0) : a.featured ? -1 : 1);
